import { faChevronDown, faLoader } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Action from '@sharedComponents/Action';
import Typography from '@sharedComponents/Typography';
import { useEffect, useRef, useState } from 'react';
import Select from 'react-select';
import { components } from 'react-select';

import * as style from './style.module.css';

const ButtonSelect = ({
    icon = null,
    options = [],
    defaultValue = null,
    status = 'primary',
    onDefaultClick = null,
    onSelect = null,
    disabled = false,
    loading = false,
}) => {
    const [menuIsOpen, setMenuIsOpen] = useState(false);
    const containerRef = useRef(null);

    useEffect(() => {
        const handleOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setMenuIsOpen(false);
            }
        };
        if (menuIsOpen) document.addEventListener('mousedown', handleOutside);
        return () => document.removeEventListener('mousedown', handleOutside);
    }, [menuIsOpen]);

    const textColor = 'branded-primary';

    // The label and chevron toggles are custom handlers: react-select's isDisabled
    // does not block them, so they must check the state themselves.
    const toggleMenu = () => {
        if (disabled || loading) return;
        setMenuIsOpen((prev) => !prev);
    };

    const handleLabelClick = !defaultValue?.action
        ? () => {
              onDefaultClick?.();
              toggleMenu();
          }
        : onDefaultClick;

    return (
        <div ref={containerRef}>
            <Select
                unstyled
                options={options}
                menuIsOpen={menuIsOpen}
                onMenuOpen={() => setMenuIsOpen(true)}
                onMenuClose={() => setMenuIsOpen(false)}
                onChange={(option) => onSelect?.(option)}
                components={{
                    DropdownIndicator: () => (
                        <div
                            className={style.dropdownIndicator}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                toggleMenu();
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    toggleMenu();
                                }
                            }}
                            role="button"
                            tabIndex={0}
                        >
                            <div className={`${style.arrowContainer} ${status === 'secondary' ? style.arrowContainerSecondary : ''}`}>
                                <FontAwesomeIcon icon={faChevronDown} className={`${status === 'secondary' && style.arrowSecondary}`} />
                            </div>
                        </div>
                    ),
                    Option: ({ children, ...props }) => {
                        const { action, icon, loading, danger } = props.data;
                        const optionColor = danger ? 'error-primary' : textColor;
                        const dangerIconStyle = danger ? { color: 'var(--text-color-error-primary)' } : undefined;
                        return (
                            <div
                                className={`${status === 'primary' ? style.option : style.optionSecondary} ${loading ? style.optionLoading : ''}`}
                            >
                                <Action
                                    action={loading ? null : action}
                                    // The custom Option bypasses react-select's selection flow,
                                    // so the menu must be closed here.
                                    onClick={loading ? undefined : () => setMenuIsOpen(false)}
                                    className={style.optionAction}
                                >
                                    {loading ? (
                                        <FontAwesomeIcon icon={faLoader} spin />
                                    ) : (
                                        icon && <FontAwesomeIcon icon={icon} style={dangerIconStyle} />
                                    )}
                                    <Typography color={optionColor} fontWeight="semi-bold">
                                        {children}
                                    </Typography>
                                </Action>
                            </div>
                        );
                    },
                    ValueContainer: ({ ...props }) => {
                        return (
                            <components.ValueContainer {...props}>
                                {status === 'secondary' ? (
                                    // The secondary variant is not a split button: the label
                                    // toggles the menu, exactly like the chevron.
                                    <div
                                        className={`${style.defaultValueSecondary}`}
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            toggleMenu();
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                toggleMenu();
                                            }
                                        }}
                                        role="button"
                                        tabIndex={0}
                                    >
                                        {loading ? <FontAwesomeIcon icon={faLoader} spin /> : icon && <FontAwesomeIcon icon={icon} />}
                                        <Typography fontWeight="semi-bold">{defaultValue.label}</Typography>
                                    </div>
                                ) : (
                                    <Action className={`${style.defaultValue}`} action={defaultValue.action} onClick={handleLabelClick}>
                                        {loading ? <FontAwesomeIcon icon={faLoader} spin /> : icon && <FontAwesomeIcon icon={icon} />}
                                        <Typography fontWeight="semi-bold">{defaultValue.label}</Typography>
                                    </Action>
                                )}
                            </components.ValueContainer>
                        );
                    },
                }}
                classNames={{
                    menu: () => `${style.menu} ${status === 'secondary' && style.menuSecondary}`,
                    menuList: () => style.menuList,
                    control: () =>
                        `${style.control} ${status === 'secondary' && style.controlSecondary} ${menuIsOpen ? style.controlOpen : ''}`,
                    dropdownIndicator: () => style.dropdownIndicator,
                }}
                isSearchable={false}
                isDisabled={disabled || loading}
                isOptionDisabled={(option) => !!option.loading}
            />
        </div>
    );
};

export default ButtonSelect;
